import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';
import { useWorkOrderPanel } from './contexts/WorkOrderPanelContext';
import useAutoRefresh from './hooks/useAutoRefresh';

const STATUS = {
  received:      { label: 'Received',    color: '#6c5ce7', icon: '📥' },
  diagnosed:     { label: 'Diagnosed',   color: '#f59e0b', icon: '🔍' },
  approved:      { label: 'Approved',    color: '#00b894', icon: '✅' },
  in_progress:   { label: 'In Progress', color: '#0984e3', icon: '🔧' },
  quality_check: { label: 'QC',          color: '#a855f7', icon: '🔬' },
  ready:         { label: 'Ready',       color: '#00cec9', icon: '🏁' },
  picked_up:     { label: 'Picked Up',   color: '#636e72', icon: '🏍️' },
  closed:        { label: 'Closed',      color: '#888', icon: '📁' },
  cancelled:     { label: 'Cancelled',   color: '#d63031', icon: '❌' },
};
const PRIORITY = { low: { color: '#888' }, normal: { color: '#0984e3' }, high: { color: '#f59e0b' }, urgent: { color: '#d63031' } };
const KANBAN = ['received', 'diagnosed', 'approved', 'in_progress', 'quality_check', 'ready'];

function getNextStatuses(s) {
  return { received: ['diagnosed','cancelled'], diagnosed: ['approved','cancelled'], approved: ['in_progress'],
    in_progress: ['quality_check','ready'], quality_check: ['ready','in_progress'], ready: ['picked_up'], picked_up: ['closed'] }[s] || [];
}

export default function WorkOrders() {
  var { openPanel: openDetail } = useWorkOrderPanel();
  var [workOrders, setWorkOrders] = useState([]);
  var [board, setBoard] = useState({});
  var [view, setView] = useState('kanban');
  var [loading, setLoading] = useState(true);
  var [showModal, setShowModal] = useState(false);
  var [customers, setCustomers] = useState([]);
  var [vehicles, setVehicles] = useState([]);
  var [staff, setStaff] = useState([]);
  var [filter, setFilter] = useState('active');
  var [search, setSearch] = useState('');

  var fetchData = useCallback(async () => {
    try {
      var [br, lr, cr, sr] = await Promise.all([
        axios.get('/api/work-orders/board').catch(() => ({ data: { board: {} } })),
        axios.get('/api/work-orders'),
        axios.get('/api/customers?limit=500').catch(() => ({ data: { customers: [] } })),
        axios.get('/api/staff').catch(() => ({ data: { staff: [] } })),
      ]);
      setBoard(br.data.board || {});
      setWorkOrders(lr.data.work_orders || []);
      setCustomers(cr.data.customers || cr.data.contacts || []);
      setStaff(sr.data.staff || sr.data.users || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, 15000);

  var loadVehicles = async (cid) => {
    if (!cid) { setVehicles([]); return; }
    try { var r = await axios.get('/api/vehicles?contact_id=' + cid); setVehicles(r.data.vehicles || []); }
    catch (e) { setVehicles([]); }
  };

  var updateStatus = async (id, s) => {
    try { await axios.put('/api/work-orders/' + id + '/status', { status: s }); fetchData(); }
    catch (e) { alert('Failed'); }
  };

  var saveWorkOrder = async (form) => {
    try {
      if (form.id) await axios.put('/api/work-orders/' + form.id, form);
      else await axios.post('/api/work-orders', form);
      setShowModal(false); fetchData();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  // Stats
  var active = workOrders.filter(w => !['picked_up','closed','cancelled'].includes(w.status));
  var urgent = active.filter(w => w.priority === 'urgent').length;
  var ready = workOrders.filter(w => w.status === 'ready').length;

  var filteredList = workOrders.filter(wo => {
    var ff = filter === 'active' ? !['picked_up','closed','cancelled'].includes(wo.status) :
      filter === 'completed' ? ['picked_up','closed'].includes(wo.status) : true;
    var q = search.toLowerCase();
    var fs = !q || (wo.order_number||'').toLowerCase().includes(q) || (wo.title||'').toLowerCase().includes(q) ||
      (wo.contacts?.first_name||'').toLowerCase().includes(q) || (wo.contacts?.last_name||'').toLowerCase().includes(q) ||
      (wo.customer_name||'').toLowerCase().includes(q) || (wo.vehicle_info||'').toLowerCase().includes(q);
    return ff && fs;
  });

  var filterCard = (wo) => {
    if (!search) return true;
    var q = search.toLowerCase();
    return (wo.order_number||'').toLowerCase().includes(q) || (wo.title||'').toLowerCase().includes(q) ||
      (wo.customer_name||'').toLowerCase().includes(q) || (wo.vehicle_info||'').toLowerCase().includes(q);
  };

  var S = {
    input: { width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', color: '#e2e2e8', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: '600', color: '#888', marginBottom: '6px', display: 'block' },
    card: { background: '#12121f', border: '1px solid #1e1e35', borderRadius: '16px' },
    pill: (a) => ({ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: a ? '#ff6b2b' : 'transparent', color: a ? '#fff' : '#888' }),
    viewBtn: (a) => ({ padding: '8px 14px', borderRadius: '8px', border: a ? '1px solid #ff6b2b' : '1px solid #1e1e35', background: a ? '#ff6b2b15' : 'transparent', color: a ? '#ff6b2b' : '#888', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }),
    badge: (c) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: c + '20', color: c }),
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888' }}><p>Loading work orders...</p></div>;

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 6px' }}>Work Orders</h1>
          <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>{workOrders.length} total · {active.length} active</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', background: '#1a1c2e', borderRadius: '10px', padding: '4px' }}>
            <button onClick={() => setView('kanban')} style={S.viewBtn(view === 'kanban')}>▦ Board</button>
            <button onClick={() => setView('list')} style={S.viewBtn(view === 'list')}>☰ List</button>
          </div>
          <button onClick={() => setShowModal(true)} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>+ New Work Order</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        {[
          { label: 'Active', value: active.length, color: '#0984e3' },
          { label: 'In Progress', value: workOrders.filter(w => w.status === 'in_progress').length, color: '#ff6b2b' },
          { label: 'Ready', value: ready, color: '#00cec9' },
          { label: 'Urgent', value: urgent, color: urgent > 0 ? '#d63031' : '#888' },
          { label: 'Completed', value: workOrders.filter(w => ['picked_up','closed'].includes(w.status)).length, color: '#00b894' },
        ].map((s, i) => (
          <div key={i} style={{ ...S.card, padding: '14px 16px' }}>
            <p style={{ fontSize: '10px', color: '#666', fontWeight: '700', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
            <p style={{ fontSize: '22px', fontWeight: '800', color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input placeholder="Search order #, customer, vehicle, title..." value={search} onChange={e => setSearch(e.target.value)} style={S.input} />
      </div>

      {/* ══════ KANBAN ══════ */}
      {view === 'kanban' && (
        <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '20px' }}>
          {KANBAN.map(status => {
            var cfg = STATUS[status];
            var items = (board[status] || []).filter(filterCard);
            return (
              <div key={status} style={{ minWidth: '200px', flex: '1 0 200px', background: '#1a1c2e', borderRadius: '14px', border: '1px solid #1e1e35', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #1e1e35', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>{cfg.icon}</span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#aaa', flex: 1 }}>{cfg.label}</span>
                  <span style={{ ...S.badge(cfg.color), fontSize: '10px' }}>{items.length}</span>
                </div>
                <div style={{ padding: '8px', flex: 1, minHeight: '80px' }}>
                  {items.length === 0 && <p style={{ textAlign: 'center', color: '#333', fontSize: '11px', padding: '16px 0' }}>Empty</p>}
                  {items.map(wo => (
                    <div key={wo.id} onClick={() => openDetail(wo.id)}
                      style={{ background: '#12121f', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px', border: '1px solid #1e1e35', cursor: 'pointer', transition: 'border-color 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = cfg.color + '60'} onMouseLeave={e => e.currentTarget.style.borderColor = '#1e1e35'}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#0984e3', fontSize: '11px', fontWeight: '700' }}>{wo.order_number}</span>
                        <span style={{ color: PRIORITY[wo.priority]?.color, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase' }}>
                          {wo.priority === 'urgent' ? '🔴' : wo.priority === 'high' ? '🟡' : ''} {wo.priority !== 'normal' ? wo.priority : ''}
                        </span>
                      </div>
                      <p style={{ color: '#fff', fontSize: '12px', fontWeight: '600', margin: '0 0 2px', lineHeight: '1.3' }}>{wo.title || 'Untitled'}</p>
                      {wo.contacts && <p style={{ color: '#888', fontSize: '11px', margin: '0 0 1px' }}>{wo.contacts.first_name} {wo.contacts.last_name}</p>}
                      {wo.vehicles && (
                        <p style={{ color: '#666', fontSize: '10px', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          🏍️ {wo.vehicles.year} {wo.vehicles.make} {wo.vehicles.model}
                          {wo.vehicles.color && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: wo.vehicles.color.toLowerCase(), border: '1px solid #333', display: 'inline-block' }} />}
                        </p>
                      )}
                      {wo.total > 0 && <p style={{ color: '#00b894', fontSize: '12px', fontWeight: '700', margin: '0 0 6px' }}>${parseFloat(wo.total).toFixed(2)}</p>}
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                        {getNextStatuses(status).map((ns, i) => (
                          <button key={ns} onClick={() => updateStatus(wo.id, ns)}
                            style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', fontSize: '10px', fontWeight: '700', cursor: 'pointer',
                              background: i === 0 ? STATUS[ns]?.color : STATUS[ns]?.color + '20', color: i === 0 ? '#fff' : STATUS[ns]?.color }}>
                            {i === 0 ? '→ ' : ''}{STATUS[ns]?.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════ LIST ══════ */}
      {view === 'list' && (
        <div>
          <div style={{ display: 'flex', gap: '3px', background: '#1a1c2e', borderRadius: '10px', padding: '4px', marginBottom: '12px', width: 'fit-content' }}>
            {['active', 'completed', 'all'].map(f => <button key={f} onClick={() => setFilter(f)} style={S.pill(filter === f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>)}
          </div>

          {filteredList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 24px', ...S.card }}>
              <p style={{ fontSize: '40px', marginBottom: '12px' }}>🔧</p>
              <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700' }}>No work orders found</p>
            </div>
          ) : (
            <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '850px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1e1e35' }}>
                      {['Order', 'Title', 'Customer', 'Vehicle', 'Status', 'Priority', ''].map(h => (
                        <th key={h} style={{ padding: '12px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map(wo => {
                      var sc = STATUS[wo.status] || {};
                      var next = getNextStatuses(wo.status);
                      return (
                        <tr key={wo.id} onClick={() => openDetail(wo.id)} style={{ borderBottom: '1px solid #1e1e3515', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#252840'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '10px' }}><span style={{ color: '#0984e3', fontWeight: '700', fontSize: '13px' }}>{wo.order_number}</span></td>
                          <td style={{ padding: '10px' }}><span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{wo.title || 'Untitled'}</span></td>
                          <td style={{ padding: '10px' }}><span style={{ color: '#aaa', fontSize: '13px' }}>{wo.contacts ? wo.contacts.first_name + ' ' + wo.contacts.last_name : '—'}</span></td>
                          <td style={{ padding: '10px' }}><span style={{ color: '#888', fontSize: '12px' }}>{wo.vehicles ? (wo.vehicles.year||'') + ' ' + (wo.vehicles.make||'') + ' ' + (wo.vehicles.model||'') : '—'}</span></td>
                          <td style={{ padding: '10px' }}><span style={S.badge(sc.color)}>{sc.icon} {sc.label}</span></td>
                          <td style={{ padding: '10px' }}><span style={{ color: PRIORITY[wo.priority]?.color, fontWeight: '700', fontSize: '11px', textTransform: 'uppercase' }}>{wo.priority}</span></td>
                          <td style={{ padding: '10px' }} onClick={e => e.stopPropagation()}>
                            {next.length > 0 && (
                              <button onClick={() => updateStatus(wo.id, next[0])}
                                style={{ padding: '5px 12px', borderRadius: '8px', border: 'none', fontSize: '11px', fontWeight: '700', cursor: 'pointer', background: STATUS[next[0]]?.color, color: '#fff' }}>
                                → {STATUS[next[0]]?.label}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '10px 14px', borderTop: '1px solid #1e1e35', fontSize: '12px', color: '#666' }}>Showing {filteredList.length} of {workOrders.length}</div>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showModal && <CreateModal customers={customers} vehicles={vehicles} staff={staff} onLoadVehicles={loadVehicles} onSave={saveWorkOrder}
        onClose={() => setShowModal(false)}
        onCustomerAdded={c => setCustomers(p => [...p, c].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')))}
        onVehicleAdded={v => setVehicles(p => [...p, v])} S={S} />}
    </div>
  );
}

// ══════════════════════════════════════════════
// CREATE MODAL
// ══════════════════════════════════════════════
function CreateModal({ customers, vehicles, staff, onLoadVehicles, onSave, onClose, onCustomerAdded, onVehicleAdded, S }) {
  var [form, setForm] = useState({ contact_id: '', vehicle_id: '', assigned_staff_id: '', title: '', description: '', priority: 'normal', estimated_completion: '', notes: '' });
  var [showNewCust, setShowNewCust] = useState(false);
  var [showNewVeh, setShowNewVeh] = useState(false);
  var [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  var [newVeh, setNewVeh] = useState({ year: '', make: '', model: '', color: '', vin: '', license_plate: '', mileage: '' });
  var [saving, setSaving] = useState(false);
  var [items, setItems] = useState([]);
  var [newItem, setNewItem] = useState({ type: 'labor', name: '', quantity: '1', unit_price: '' });
  var [inventory, setInventory] = useState([]);
  var [partSearch, setPartSearch] = useState('');
  var [showInv, setShowInv] = useState(false);
  var [custSearch, setCustSearch] = useState('');

  var u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => { if (form.contact_id) onLoadVehicles(form.contact_id); }, [form.contact_id]);
  useEffect(() => { axios.get('/api/inventory').then(r => setInventory(r.data.products || [])).catch(() => {}); }, []);

  var filteredCusts = customers.filter(c => !custSearch || (c.first_name + ' ' + (c.last_name || '') + ' ' + (c.phone || '')).toLowerCase().includes(custSearch.toLowerCase())).slice(0, 12);
  var selectedCust = customers.find(c => String(c.id) === String(form.contact_id));

  var createCustomer = async () => {
    if (!newCust.first_name) return; setSaving(true);
    try { var r = await axios.post('/api/customers', newCust); var c = r.data.customer; if (c && onCustomerAdded) onCustomerAdded(c); u('contact_id', c.id); setShowNewCust(false); setNewCust({ first_name: '', last_name: '', phone: '', email: '' }); }
    catch (e) { alert('Failed'); } setSaving(false);
  };

  var createVehicle = async () => {
    if (!newVeh.make) return; setSaving(true);
    try { var p = { ...newVeh, contact_id: form.contact_id }; if (p.year) p.year = parseInt(p.year); if (p.mileage) p.mileage = parseInt(p.mileage);
      var r = await axios.post('/api/vehicles', p); var v = r.data.vehicle; if (v && onVehicleAdded) onVehicleAdded(v); u('vehicle_id', v.id); setShowNewVeh(false); setNewVeh({ year: '', make: '', model: '', color: '', vin: '', license_plate: '', mileage: '' }); }
    catch (e) { alert('Failed'); } setSaving(false);
  };

  var addItem = () => { if (!newItem.name || !newItem.unit_price) return; setItems(p => [...p, { ...newItem, quantity: parseFloat(newItem.quantity) || 1, unit_price: parseFloat(newItem.unit_price) || 0 }]); setNewItem({ type: 'labor', name: '', quantity: '1', unit_price: '' }); };
  var addFromInv = (p) => { setItems(prev => [...prev, { type: 'part', name: p.name, quantity: 1, unit_price: parseFloat(p.price || 0) }]); setPartSearch(''); setShowInv(false); };
  var itemsTotal = items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
  var handleSave = () => onSave({ ...form, items: items.length > 0 ? items : undefined });

  var qaBar = { padding: '14px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', marginTop: '8px' };
  var qaTitle = { fontSize: '12px', fontWeight: '700', color: '#ff6b2b', marginBottom: '8px' };

  return (
    <Overlay onClose={onClose}>
      <div style={{ maxHeight: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: 0 }}>New Work Order</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: '20px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'grid', gap: '14px' }}>
            {/* Customer */}
            <div>
              <label style={S.label}>Customer *</label>
              {selectedCust && !custSearch ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ ...S.input, flex: 1, borderColor: '#00b894', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#fff' }}>{selectedCust.first_name} {selectedCust.last_name} {selectedCust.phone ? '· ' + selectedCust.phone : ''}</span>
                    <span style={{ color: '#00b894' }}>✓</span>
                  </div>
                  <button onClick={() => { u('contact_id', ''); setCustSearch(' '); }} style={{ padding: '12px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', cursor: 'pointer', fontSize: '12px' }}>Change</button>
                  <button onClick={() => setShowNewCust(!showNewCust)} style={{ padding: '12px', background: showNewCust ? '#ff6b2b' : '#1e1e35', border: 'none', borderRadius: '10px', color: showNewCust ? '#fff' : '#aaa', cursor: 'pointer', fontSize: '12px' }}>{showNewCust ? '✕' : '+ New'}</button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input placeholder="Search customer..." value={custSearch} onChange={e => setCustSearch(e.target.value)} style={{ ...S.input, flex: 1 }} autoFocus />
                    <button onClick={() => setShowNewCust(!showNewCust)} style={{ padding: '12px', background: showNewCust ? '#ff6b2b' : '#1e1e35', border: 'none', borderRadius: '10px', color: showNewCust ? '#fff' : '#aaa', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>{showNewCust ? '✕' : '+ New'}</button>
                  </div>
                  <div style={{ maxHeight: '120px', overflowY: 'auto', marginTop: '4px' }}>
                    {filteredCusts.map(c => (
                      <div key={c.id} onClick={() => { u('contact_id', c.id); setCustSearch(''); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #1e1e3520' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#252840'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>{c.first_name} {c.last_name}</span>
                        <span style={{ color: '#666', fontSize: '12px' }}>{c.phone || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {showNewCust && (
                <div style={qaBar}>
                  <p style={qaTitle}>➕ Quick Add Customer</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input style={S.input} placeholder="First name *" value={newCust.first_name} onChange={e => setNewCust(p => ({ ...p, first_name: e.target.value }))} />
                    <input style={S.input} placeholder="Last name" value={newCust.last_name} onChange={e => setNewCust(p => ({ ...p, last_name: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input style={S.input} placeholder="Phone" value={newCust.phone} onChange={e => setNewCust(p => ({ ...p, phone: e.target.value }))} />
                    <input style={S.input} placeholder="Email" value={newCust.email} onChange={e => setNewCust(p => ({ ...p, email: e.target.value }))} />
                  </div>
                  <button onClick={createCustomer} disabled={saving || !newCust.first_name} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: newCust.first_name ? '#00b894' : '#333', color: '#fff', fontWeight: '700', cursor: newCust.first_name ? 'pointer' : 'not-allowed' }}>{saving ? 'Creating...' : '✓ Create & Select'}</button>
                </div>
              )}
            </div>

            {/* Vehicle */}
            <div>
              <label style={S.label}>Vehicle</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select value={form.vehicle_id} onChange={e => u('vehicle_id', e.target.value)} style={{ ...S.input, flex: 1, cursor: 'pointer' }}>
                  <option value="">Select vehicle...</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} {v.license_plate ? '(' + v.license_plate + ')' : ''}</option>)}
                </select>
                {form.contact_id && <button onClick={() => setShowNewVeh(!showNewVeh)} style={{ padding: '12px', background: showNewVeh ? '#ff6b2b' : '#1e1e35', border: 'none', borderRadius: '10px', color: showNewVeh ? '#fff' : '#aaa', cursor: 'pointer', fontSize: '12px' }}>{showNewVeh ? '✕' : '+ New'}</button>}
              </div>
              {showNewVeh && (
                <div style={qaBar}>
                  <p style={qaTitle}>🏍️ Quick Add Vehicle</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input style={S.input} placeholder="Year" value={newVeh.year} onChange={e => setNewVeh(p => ({ ...p, year: e.target.value }))} />
                    <input style={S.input} placeholder="Make *" value={newVeh.make} onChange={e => setNewVeh(p => ({ ...p, make: e.target.value }))} />
                    <input style={S.input} placeholder="Model" value={newVeh.model} onChange={e => setNewVeh(p => ({ ...p, model: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input style={S.input} placeholder="Color" value={newVeh.color} onChange={e => setNewVeh(p => ({ ...p, color: e.target.value }))} />
                    <input style={S.input} placeholder="VIN" value={newVeh.vin} onChange={e => setNewVeh(p => ({ ...p, vin: e.target.value }))} />
                    <input style={S.input} placeholder="Plate" value={newVeh.license_plate} onChange={e => setNewVeh(p => ({ ...p, license_plate: e.target.value }))} />
                  </div>
                  <button onClick={createVehicle} disabled={saving || !newVeh.make} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: newVeh.make ? '#00b894' : '#333', color: '#fff', fontWeight: '700', cursor: newVeh.make ? 'pointer' : 'not-allowed' }}>{saving ? 'Creating...' : '✓ Create & Select'}</button>
                </div>
              )}
            </div>

            <div><label style={S.label}>Title *</label><input value={form.title} onChange={e => u('title', e.target.value)} placeholder="e.g. Full service + chain replacement" style={S.input} /></div>
            <div><label style={S.label}>Description</label><textarea value={form.description} onChange={e => u('description', e.target.value)} placeholder="Describe the work needed..." rows={3} style={{ ...S.input, resize: 'vertical' }} /></div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div><label style={S.label}>Technician</label><select value={form.assigned_staff_id} onChange={e => u('assigned_staff_id', e.target.value)} style={{ ...S.input, cursor: 'pointer' }}>
                <option value="">Unassigned</option>{staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}</select></div>
              <div><label style={S.label}>Priority</label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['low', 'normal', 'high', 'urgent'].map(p => (
                    <button key={p} onClick={() => u('priority', p)} style={{ flex: 1, padding: '10px 4px', borderRadius: '8px', border: form.priority === p ? '1px solid ' + PRIORITY[p].color : '1px solid #1e1e35', background: form.priority === p ? PRIORITY[p].color + '20' : '#1a1c2e', color: form.priority === p ? PRIORITY[p].color : '#666', fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'capitalize' }}>{p}</button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div><label style={S.label}>Est. Completion</label><input type="date" value={form.estimated_completion} onChange={e => u('estimated_completion', e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Notes</label><input value={form.notes} onChange={e => u('notes', e.target.value)} placeholder="Internal notes..." style={S.input} /></div>
            </div>

            {/* Line Items */}
            <div style={{ background: '#1a1c2e', borderRadius: '12px', border: '1px solid #1e1e35', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ ...S.label, margin: 0 }}>Line Items {items.length > 0 && '(' + items.length + ')'}</label>
                <span style={{ fontSize: '14px', fontWeight: '700', color: '#00b894' }}>{itemsTotal > 0 ? '$' + itemsTotal.toFixed(2) : ''}</span>
              </div>

              {items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #1e1e3540' }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: '#666', textTransform: 'uppercase', width: '36px' }}>{item.type}</span>
                  <span style={{ flex: 1, fontSize: '12px', fontWeight: '600', color: '#aaa' }}>{item.name}</span>
                  <span style={{ fontSize: '11px', color: '#666' }}>{item.quantity} × ${item.unit_price.toFixed(2)}</span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff', width: '60px', textAlign: 'right' }}>${(item.quantity * item.unit_price).toFixed(2)}</span>
                  <button onClick={() => setItems(p => p.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#d63031', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                </div>
              ))}

              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 50px 70px auto', gap: '6px', marginTop: '8px', alignItems: 'center' }}>
                <select value={newItem.type} onChange={e => setNewItem(p => ({ ...p, type: e.target.value }))} style={{ ...S.input, padding: '8px 4px', fontSize: '11px' }}>
                  <option value="labor">Labor</option><option value="part">Part</option><option value="fee">Fee</option>
                </select>
                <input placeholder="Description" value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addItem()} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                <input placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))} style={{ ...S.input, padding: '8px', fontSize: '12px', textAlign: 'center' }} />
                <input placeholder="Price" value={newItem.unit_price} onChange={e => setNewItem(p => ({ ...p, unit_price: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addItem()} style={{ ...S.input, padding: '8px', fontSize: '12px' }} />
                <button onClick={addItem} disabled={!newItem.name || !newItem.unit_price} style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', background: (newItem.name && newItem.unit_price) ? '#ff6b2b' : '#333', color: '#fff', fontWeight: '700', fontSize: '12px', cursor: (newItem.name && newItem.unit_price) ? 'pointer' : 'not-allowed' }}>+</button>
              </div>

              <div style={{ marginTop: '8px' }}>
                <button onClick={() => setShowInv(!showInv)} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #00b894', background: showInv ? '#00b894' : '#00b89415', color: showInv ? '#fff' : '#00b894', fontWeight: '600', fontSize: '11px', cursor: 'pointer' }}>
                  {showInv ? '✕ Close' : '📦 Add from Inventory'}
                </button>
                {showInv && (
                  <div style={{ marginTop: '8px' }}>
                    <input placeholder="Search parts..." value={partSearch} onChange={e => setPartSearch(e.target.value)} style={{ ...S.input, padding: '8px 12px', fontSize: '12px', marginBottom: '6px' }} />
                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {inventory.filter(p => !partSearch || p.name.toLowerCase().includes(partSearch.toLowerCase()) || (p.sku || '').toLowerCase().includes(partSearch.toLowerCase())).slice(0, 15).map(p => (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #1e1e3520' }}>
                          <div><p style={{ color: '#aaa', fontSize: '12px', fontWeight: '600', margin: 0 }}>{p.name}</p><p style={{ color: '#555', fontSize: '10px', margin: 0 }}>{p.sku && p.sku + ' · '}${parseFloat(p.price || 0).toFixed(2)}{p.current_stock != null && ' · ' + p.current_stock + ' in stock'}</p></div>
                          <button onClick={() => addFromInv(p)} style={{ padding: '4px 10px', background: '#00b89420', color: '#00b894', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '10px', cursor: 'pointer' }}>+ Add</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #1e1e35', display: 'flex', gap: '12px', flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '14px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>Create{itemsTotal > 0 ? ' ($' + itemsTotal.toFixed(2) + ')' : ''}</button>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '30px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflow: 'auto' }}>{children}</div>
    </div>
  );
}
